import { useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  InformationIcon,
  LuxButton,
  LuxTypography,
  Stack,
  useMediaQuery,
  WarningIcon,
  useTheme,
} from '@gemini/fe-ds-core';

import { ExternalWrapper } from '../Steps/Steps.styles';

import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { useHistory } from 'react-router-dom';
import { AppointmentResponse } from '@utils/interfaces';
import { ErrorCodes } from '@utils/enums/ErrorCodes.enum';
import {
  AppointmentPageCard,
  ButtonsWrapper,
  CardTextWrapper,
  ErrorWrapper,
  IconTitleWrapper,
} from './ThankYouPage.styles';
// import { usePatientStore } from '@store/patientStore';
import { AxiosError } from 'axios';
import { PutAppointmentError } from '@hooks/usePutAppointment';
import { Patient, usePatientStore } from '@store/patientStore';
import { matchPatientWithSlotIds, resetApp } from '@utils/functions';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useUrlsContext } from '@utils/contexts/UrlsContext';

export interface AppointmentErrorPageProps {
  error?: AxiosError<PutAppointmentError>;
  res?: AppointmentResponse;
  errorType: ErrorCodes;
}

const AppointmentErrorPage = ({
  res,
  errorType,
}: AppointmentErrorPageProps) => {
  const { t } = useTranslation();
  const { Urls } = useUrlsContext();

  const theme = useTheme();

  const brandName = useOnlineBookingStore(state => state.brandName);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const patients = usePatientStore(state => state.patients);
  const getGeneralInfo = useOnlineBookingStore(state => state.getGeneralInfo);

  const resetScheduleExam = usePatientStore(state => state.resetScheduleExam);

  const history = useHistory();

  const setFormStep = useOnlineBookingStore(state => state.setFormStep);
  const setIsRescheduling = useOnlineBookingStore(
    state => state.setIsRescheduling,
  );
  const setShowTyp = useOnlineBookingStore(state => state.setShowTyp);
  const setShowAddPatientFrame = useOnlineBookingStore(
    state => state.setShowAddPatientFrame,
  );

  // funzione per ripartire dallo step di app type
  const restartFlow = () => {
    history.push(Urls.APPOINTMENT_TYPE);
    setFormStep(StepsStep.APPOINTMENT_TYPE);
    setShowAddPatientFrame(false);
    patients.forEach((patient, index) => {
      if (patient) {
        console.log(
          '#use case reschedule remove schedule for all patients: ',
          patient,
        );
        resetScheduleExam(index);
      }
    });
  };

  // funzione per ripartire dallo step di schedule con tutto bloccato
  const handleReschedule = () => {
    setIsRescheduling(true);
    setShowAddPatientFrame(false);
    // remove scheduleExam for the failed appointments
    patients.forEach((patient, index) => {
      if (
        patient &&
        (!patient.appointmentId || patient.appointmentId === undefined)
      ) {
        console.log(
          '#use case reschedule remove schedule for patient: ',
          patient,
        );
        resetScheduleExam(index);
      }
    });

    history.push(Urls.SCHEDULE_EXAM);
    setFormStep(StepsStep.SCHEDULE_EXAM);
  };

  // funzione per procedere con i soli pazienti in success --> mostro typ
  const handleProceed = () => {
    setShowTyp(true);
  };

  const getPatientDescription = useCallback((patient: Patient | undefined) => {
    if (
      patient?.patientDetails?.firstName &&
      patient?.patientDetails?.lastName &&
      patient?.scheduleExam?.day &&
      patient?.scheduleExam?.hour &&
      patient?.scheduleExam?.doctor
    ) {
      return (
        <LuxTypography variant="textMdRegular" fontWeight={700}>
          {patient.patientDetails.firstName +
            ' ' +
            patient.patientDetails.lastName +
            ', ' +
            dayjs(patient.scheduleExam.day).format('dddd, MMM D, YYYY') +
            ' at ' +
            patient.scheduleExam.hour +
            ' (' +
            patient.scheduleExam.doctor +
            ')'}
        </LuxTypography>
      );
    }
    return '';
  }, []);

  //callback per generare le cart di success e fail//callback per generare le card di success e fail
  const generateTags = useCallback(
    (res?: AppointmentResponse) => {
      if (res) {
        const getPatientSlotIds = (p: Patient | undefined) =>
          p?.scheduleExam?.slotId?.slotId
            ? [p.scheduleExam.slotId.slotId]
            : p?.scheduleExam?.slotId?.internalSlots.map(s => s.slotId);

        // success
        const successedPts = patients.filter(p => {
          const patientSlotId = getPatientSlotIds(p);
          if (patientSlotId && p) {
            return (
              res.findIndex(
                r =>
                  matchPatientWithSlotIds(patientSlotId, r.slotIds) &&
                  r.success,
              ) > -1
            );
          }
          return false;
        });

        // unsuccess: conserva anche l'error.code
        const unsuccessedPts = patients.reduce<
          Array<{ patient: Patient; errorCode?: string }>
        >((acc, p) => {
          if (!p) return acc;
          const patientSlotId = getPatientSlotIds(p);
          if (!patientSlotId) return acc;

          const matched = res.find(
            r =>
              matchPatientWithSlotIds(patientSlotId, r.slotIds) && !r.success,
          );
          if (matched) {
            acc.push({ patient: p, errorCode: matched.error?.code });
          }
          return acc;
        }, []);

        const allFailed = errorType === ErrorCodes.ALL;

        return (
          <Stack flexDirection="column" gap="1rem" padding="1rem" width="100%">
            {successedPts.length > 0 && !allFailed && (
              <AppointmentPageCard variant="green">
                <CheckCircleIcon size="medium" />
                <CardTextWrapper>
                  <LuxTypography variant="textMdRegular">
                    {t('appointmentErrorPage.successTitle')}
                  </LuxTypography>
                  {successedPts.length > 1 ? (
                    <ul>
                      {successedPts.map(sp => (
                        <li key={sp?.reservationId}>
                          {getPatientDescription(sp)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <LuxTypography variant="textMdRegular">
                      {getPatientDescription(successedPts[0])}
                    </LuxTypography>
                  )}
                </CardTextWrapper>
              </AppointmentPageCard>
            )}

            {unsuccessedPts.length > 0 && (
              <AppointmentPageCard variant="red">
                <WarningIcon size="medium" />
                <CardTextWrapper>
                  <LuxTypography variant="textMdRegular">
                    {t('appointmentErrorPage.failTitle')}
                  </LuxTypography>
                  {unsuccessedPts.length > 1 ? (
                    <ul>
                      {unsuccessedPts.map(usp => (
                        <li key={usp.patient?.reservationId}>
                          <Stack>
                            {getPatientDescription(usp.patient)}
                            {/* mapping error code from BFF with relative keylabel error message */}
                            {usp.errorCode && (
                              <LuxTypography
                                variant="textMdRegular"
                                fontWeight={700}
                              >
                                {t(`errors.${usp.errorCode}`)}
                              </LuxTypography>
                            )}
                          </Stack>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <>
                      <LuxTypography variant="textMdRegular">
                        {getPatientDescription(unsuccessedPts[0].patient)}
                      </LuxTypography>
                      {/* mapping error code from BFF with relative keylabel error message */}
                      {unsuccessedPts[0].errorCode && (
                        <LuxTypography variant="textMdRegular" fontWeight={700}>
                          {t(`errors.${unsuccessedPts[0].errorCode}`)}
                        </LuxTypography>
                      )}
                    </>
                  )}
                </CardTextWrapper>
              </AppointmentPageCard>
            )}

            <LuxTypography variant="textMdRegular" textAlign="center">
              {allFailed
                ? unsuccessedPts.length === 1
                  ? t('appointmentErrorPage.plsRescheduleApp')
                  : t('appointmentErrorPage.plsRescheduleApps')
                : t('appointmentErrorPage.reschduleOrProceed')}
            </LuxTypography>

            <ButtonsWrapper allFailed={allFailed}>
              {!allFailed && (
                <LuxButton variant="secondary" onClick={() => handleProceed()}>
                  <LuxTypography
                    variant={'textMdRegular'}
                    fontWeight={brandName && '700!important'}
                  >
                    {t('appointmentErrorPage.proceedWithConfirmed')}
                  </LuxTypography>
                </LuxButton>
              )}
              <LuxButton
                onClick={() => (allFailed ? restartFlow() : handleReschedule())}
              >
                <LuxTypography
                  variant={'textMdRegular'}
                  fontWeight={brandName && '700!important'}
                >
                  {t('appointmentErrorPage.rescheduleApps')}
                </LuxTypography>
              </LuxButton>
            </ButtonsWrapper>
          </Stack>
        );
      }
      return <></>;
    },
    [
      patients,
      errorType,
      t,
      handleProceed,
      restartFlow,
      handleReschedule,
      getPatientDescription,
    ],
  );

  useEffect(() => {
    return () => {
      if (
        errorType === ErrorCodes.BLACKLIST ||
        errorType === ErrorCodes.SERVICE
      )
        resetApp(true);
    };
  }, []);

  return (
    <ExternalWrapper
      backgroundColor={
        brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
      }
      style={{ padding: '1.5rem' }}
    >
      <ErrorWrapper isDesktop={isDesktop}>
        <IconTitleWrapper>
          <Stack
            style={{
              width: '2.75rem',
              height: '2.75rem',
              borderRadius: '100%',
              border: `1px solid ${errorType === ErrorCodes.MISC ? theme.palette.LUX.obTokens.bordersColorFunctionalWarning : theme.palette.LUX.obTokens.bordersColorFunctionalError}`,
              backgroundColor: `${errorType === ErrorCodes.MISC ? theme.palette.LUX.obTokens.bgColorFunctionalWarning : theme.palette.LUX.obTokens.bgColorFunctionalError}`,
            }}
            justifyContent="center"
            alignItems="center"
          >
            <WarningIcon size="medium" />
          </Stack>
          <LuxTypography variant="heading2">
            {t('appointmentErrorPage.attention')}
          </LuxTypography>
        </IconTitleWrapper>
        <LuxTypography variant="heading4">
          {t('appointmentErrorPage.encountProblem')}
        </LuxTypography>
        {/* GENERIC ERROR TODO: separare logica? */}
        {(errorType === ErrorCodes.GENERIC ||
          errorType === ErrorCodes.SERVICE ||
          errorType === ErrorCodes.BLACKLIST) && (
          <AppointmentPageCard variant="red">
            <InformationIcon size="medium" />
            <CardTextWrapper>
              {errorType === ErrorCodes.BLACKLIST ? (
                <>
                  <LuxTypography variant="textMedium">
                    {t('appointmentErrorPage.appointmentDenied')}
                  </LuxTypography>
                  <LuxTypography variant="textMdRegular">
                    {t('appointmentErrorPage.blacklist', {
                      storeName: getGeneralInfo()?.name,
                    })}
                    <a
                      href={`tel:${getGeneralInfo()?.phoneNumber}`}
                      onClick={() =>
                        (window.location.href = `tel:${getGeneralInfo()?.phoneNumber}`)
                      }
                    >
                      {getGeneralInfo()?.phoneNumber}
                    </a>
                  </LuxTypography>
                </>
              ) : (
                <LuxTypography variant="textMdRegular">
                  {t('appointmentErrorPage.tryLater')}
                </LuxTypography>
              )}
            </CardTextWrapper>
          </AppointmentPageCard>
        )}
        {errorType === ErrorCodes.GENERIC ||
          (errorType === ErrorCodes.SERVICE && (
            <LuxButton onClick={() => restartFlow()}>
              <LuxTypography
                variant={'textMdRegular'}
                fontWeight={brandName && '700!important'}
              >
                {t('appointmentErrorPage.restartBooking')}
              </LuxTypography>
            </LuxButton>
          ))}
        {/* ALL PATIENTS FAILED */}
        {(errorType === ErrorCodes.ALL || errorType === ErrorCodes.MISC) && (
          <>{generateTags(res)}</>
        )}
      </ErrorWrapper>
    </ExternalWrapper>
  );
};

export default AppointmentErrorPage;
